import React, { useEffect, useState, useContext } from "react";
import styled, { keyframes } from "styled-components";
import Layout from "../../components/layout";
import SEO from "../../components/seo";
import Footer from "../../components/layout/Footer";
import { Helmet } from "react-helmet";
import FlashOnIcon from '@material-ui/icons/FlashOn';
import MapIcon from '@material-ui/icons/Map';
import EuroSymbolIcon from '@material-ui/icons/EuroSymbol';
import LockIcon from '@material-ui/icons/Lock';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import HeadsetMicIcon from '@material-ui/icons/HeadsetMic';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import googlePlay from "../../images/app/google_play.png";
import appStore from "../../images/app/app_store.png";
import appPromoImg from "../../images/app/app_promo-no-bg.png";
import { AppContext } from "../../contexts/AppContext";
import { navigate } from 'gatsby';

/* ======= NOTA: strings/arrays vêm de i18n (app.page) ======= */

/* ======= BACKGROUND GLOBAL DA PÁGINA (igual ao hero) ======= */
const PageWrap = styled.div`
  background:
    radial-gradient(900px 360px at 15% 0%, #EAF6D6 0%, transparent 60%),
    radial-gradient(900px 360px at 85% 10%, #DDF0BE 0%, transparent 60%),
    #f8fafc;
`;

const Section = styled.section`
  max-width: 1100px;
  margin: 80px auto; /* mais espaçamento entre secções */
  padding: 0 20px;
`;

/* ======= TÍTULOS UNIFORMES ======= */
const SectionTitle = styled.h2`
  font-size: 2rem;
  line-height: 1.25;
  margin: 0 0 24px;
  letter-spacing: -0.01em;
  color: #0f172a;
  text-align: center;

  &:after {
    content: "";
    display: block;
    width: 72px;
    height: 4px;
    background: linear-gradient(90deg, #A5D65D, #A5D65D);
    border-radius: 999px;
    margin: 14px auto 0;
  }
`;

/* ======= SLIDER DE TESTEMUNHOS ======= */
const SliderFrame = styled.div`
  position: relative;
  max-width: 900px;
  margin: 0 auto;
  min-height: 170px; /* garante altura estável durante transições */
`;

const Slide = styled.blockquote`
  background: #0f172a;
  color: #e2e8f0;
  border-radius: 16px;
  padding: 28px;
  line-height: 1.7;
  box-shadow: inset 0 0 0 1px rgba(255,255,255,.06);
  position: absolute;
  top: 0; left: 0; right: 0;
  opacity: ${props => props.active ? 1 : 0};
  transform: translateY(${props => props.active ? '0' : '8px'});
  transition: opacity .6s ease, transform .6s ease;
`;

const NavBtn = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px; height: 40px;
  border-radius: 999px;
  border: 1px solid rgba(226,232,240,.6);
  background: rgba(255,255,255,.85);
  color: #0f172a;
  display: inline-flex; align-items: center; justify-content: center;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(2,6,23,.12);
  transition: transform .15s ease, box-shadow .15s ease;
  outline: none;

  &:hover { transform: translateY(-50%) scale(1.06); box-shadow: 0 10px 24px rgba(2,6,23,.18); }

  &.prev { left: -20px; }
  &.next { right: -20px; }

  @media (max-width: 960px) {
    &.prev { left: -6px; }
    &.next { right: -6px; }
  }
`;

const Dots = styled.div`
  display: flex; gap: 10px; justify-content: center; margin-top: 16px;
`;

const Dot = styled.button`
  width: 10px; height: 10px; border-radius: 999px; border: none; cursor: pointer;
  background: ${p => p.active ? '#A5D65D' : '#cbd5e1'};
  box-shadow: ${p => p.active ? '0 0 0 4px rgba(165,214,93,.18)' : 'none'};
  transition: background .2s ease, box-shadow .2s ease, transform .2s ease;
  &:hover { transform: scale(1.12); }
`;



/* ===================== NOVO HERO — CENTRAL, EFEITOS SUAVES ===================== */
const floatOrb = keyframes`
  0% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-12px) scale(1.03); }
  100% { transform: translateY(0) scale(1); }
`;

const Hero = styled.section`
  position: relative;
  overflow: hidden;
  padding: 140px 20px 120px;
  min-height: 78vh;
  display: flex;
  align-items: center;
  color: #0f172a;
  background:
    radial-gradient(900px 360px at 15% 0%, #EAF6D6 0%, transparent 60%),
    radial-gradient(900px 360px at 85% 10%, #DDF0BE 0%, transparent 60%),
    #f8fafc;

  .orb {
    position: absolute;
    border-radius: 999px;
    filter: blur(40px);
    opacity: .5;
    animation: ${floatOrb} 10s ease-in-out infinite;
    pointer-events: none;
  }
  .o1 { width: 260px; height: 260px; left: -60px; top: -40px; background: #D7F0A6; }
  .o2 { width: 200px; height: 200px; right: 10%; top: 8%; background: #C8E985; animation-delay: -3s; }
  .o3 { width: 220px; height: 220px; left: 12%; bottom: -40px; background: #E5F7C2; animation-delay: -6s; }

  @media (prefers-reduced-motion: reduce) {
    .orb { animation: none; }
  }
`;

const HeroInner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 960px;
  margin: 0 auto;
  text-align: center;
`;

const HeroHeading = styled.h1`
  margin: 0 0 16px;
  font-size: 3rem;
  letter-spacing: -0.02em;
  background: linear-gradient(90deg, #0f172a, #A5D65D, #A5D65D);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  text-shadow: 0 8px 30px rgba(165,214,93,.18);

  @media (max-width: 900px) { font-size: 2.4rem; }
`;

const HeroLead = styled.p`
  margin: 0 auto 28px;
  color: #334155;
  max-width: 760px;
  font-size: 1.15rem;
`;

/* imagem do mock passa a estar no background do hero */

/* ======= COMO COMEÇAR – STEPPER ======= */
const Stepper = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 28px;

  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const Step = styled.div`
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 8px 20px rgba(2,6,23,.06);
  border: 1px solid rgba(2,6,23,.06);
  padding: 26px 22px;
  position: relative;
`;

const StepNumber = styled.div`
  position: absolute;
  top: -14px; left: -14px;
  width: 40px; height: 40px;
  border-radius: 999px;
  background: #A5D65D;
  color: #fff;
  font-weight: 700;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 8px 18px rgba(165,214,93,.35);
`;

/* ======= FAQ Melhorado ======= */
const FaqList = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const FaqItem = styled.details`
  background: #fff;
  border-radius: 12px;
  padding: 18px 22px;
  margin-bottom: 14px;
  box-shadow: 0 6px 16px rgba(2,6,23,.06);
  border: 1px solid rgba(2,6,23,.06);

  summary {
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    list-style: none;
  }

  summary::-webkit-details-marker { display: none; }

  summary:after {
    content: "+";
    width: 28px; height: 28px;
    border-radius: 8px;
    background: #f1f5f9;
    color: #0f172a;
    display: inline-flex; align-items: center; justify-content: center;
    font-weight: 700;
    transition: transform .2s ease;
    margin-left: 16px;
  }

  &[open] summary:after { content: "–"; transform: rotate(0deg); }

  p { margin: 12px 0 0; color: #475569; }
`;

const CTAWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  flex-wrap: wrap;
  @media (max-width: 900px) { justify-content: center; }

  img {
    max-width: 180px;
    height: auto;
    filter: drop-shadow(0 10px 24px rgba(0,0,0,.25));
    transition: transform .2s ease, filter .2s ease;
  }
  a:hover img { transform: translateY(-2px) scale(1.02); filter: drop-shadow(0 14px 30px rgba(0,0,0,.35)); }
`;



/* ===================== FEATURES ===================== */
const Features = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 28px;

  @media (max-width: 1000px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 680px) { grid-template-columns: 1fr; }
`;

const GlassCard = styled.article`
  position: relative;
  background: rgba(255,255,255,.75);
  border-radius: 18px;
  padding: 26px 22px;
  box-shadow: 0 10px 30px rgba(2,6,23,.08);
  backdrop-filter: blur(8px);
  transform: translateZ(0);
  transition: transform .2s ease, box-shadow .2s ease;
  border: 1px solid rgba(2,6,23,.06);

  &:hover { transform: translateY(-6px); box-shadow: 0 16px 40px rgba(2,6,23,.12); }

  h3 { margin: 6px 0 8px; }
  p { margin: 0; color: #4b5563; }

  .icon {
    width: 44px; height: 44px;
    display: inline-flex; align-items: center; justify-content: center;
    border-radius: 10px;
    background: linear-gradient(135deg, #F2FBE6, #F7FDEB);
    color: #A5D65D;
    margin-bottom: 10px;
    box-shadow: 0 6px 14px rgba(165,214,93,.22);
  }
`;

/* ======= Callout para utilizadores do cartão eVaz ======= */
const CardCallout = styled.div`
  position: relative;
  background: linear-gradient(135deg, #ecfeff, #f0fdf4);
  border: 1px solid rgba(2,6,23,.06);
  border-left: 8px solid #A5D65D; /* destaque com a cor primária */
  border-radius: 18px;
  padding: 32px 28px;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 20px;
  align-items: center;
  box-shadow: 0 14px 36px rgba(2,6,23,.10);

  @media (max-width: 900px) {
    grid-template-columns: 1fr; text-align: center;
  }

  .kicker { font-weight: 800; color: #A5D65D; letter-spacing: .08em; font-size: .95rem; }
  h3 { margin: 8px 0 10px; color: #0f172a; font-size: 1.4rem; }
  p { margin: 0; color: #334155; }

  .iconCircle {
    position: absolute;
    top: -16px; left: -16px;
    width: 54px; height: 54px;
    border-radius: 999px;
    background: #A5D65D;
    color: #0b1324;
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 12px 28px rgba(165,214,93,.35);
  }
`;

/* (removidos estilos não usados: ContentBlock, BulletList) */

/* ======= SHOWCASE "Sobre a eVaz Energy" com imagem de ecrãs em baixo ======= */
const Showcase = styled.section`
  position: relative;
  max-width: 1100px;
  margin: 80px auto;
  padding: 48px 28px 0px;
  background: linear-gradient(135deg, #F2FBE6, #FFFFFF);
  border: 1px solid rgba(2,6,23,.06);
  border-radius: 24px;
  box-shadow: 0 18px 44px rgba(2,6,23,.10);
  overflow: hidden;

  &:after {
    content: "";
    position: absolute;
    width: 280px; height: 280px;
    bottom: -80px; right: -80px;
    background: radial-gradient(closest-side, rgba(165,214,93,.35), transparent 70%);
    filter: blur(6px);
  }
`;

const ShowcaseInner = styled.div`
  display: grid;
  grid-template-columns: 1fr; /* remover segunda coluna após retirar bullets */
  gap: 28px;
  align-items: start;
  position: relative; z-index: 1; /* garante que o texto fica acima dos ecrãs */
`;

const KickerPill = styled.span`
  display: inline-block;
  background: #A5D65D;
  color: #0b1324;
  font-weight: 800;
  letter-spacing: .08em;
  font-size: .8rem;
  padding: 6px 10px;
  border-radius: 999px;
`;

const ShowcaseTitle = styled.h2`
  margin: 10px 0 12px;
  font-size: 2rem;
  letter-spacing: -0.02em;
  color: #0f172a;
`;

const ShowcaseText = styled.div`
  color: #334155; line-height: 1.85; font-size: 1.05rem;
  p { margin: 0 0 12px; }
`;

/* (removido estilo não usado: ShowcaseBullets) */

const ScreensBar = styled.div`
  left: 0; right: 0; /* ocupar toda a largura para permitir centralização perfeita */
  bottom: 0; /* colada ao fundo da div */
  width: 100%;
  display: flex; justify-content: center; align-items: flex-end;
  z-index: 0; /* fica atrás do conteúdo, mas sem o cobrir graças ao padding-bottom */
  img { max-width: 880px; width: 92%; height: auto; margin: 0 auto; filter: drop-shadow(0 26px 48px rgba(2,6,23,.35)); display: block; }
`;

/* Resumo em 3 linhas + keywords com borda */
const ShowcaseSummary = styled.div`
  position: relative; z-index: 1;
  max-width: 900px; margin: 22px auto 0; text-align: center;
  color: #0f172a;
  p { margin: 0; font-size: 1.15rem; line-height: 1.75; }
`;

const Chips = styled.div`
  display: flex; flex-wrap: wrap; gap: 10px; justify-content: center;
  margin-top: 14px;
`;

const Chip = styled.span`
  display: inline-block;
  padding: 6px 12px;
  border: 1.5px solid #A5D65D;
  border-radius: 999px;
  font-weight: 600;
  color: #0f172a;
  background: rgba(165,214,93,.08);
`;

export default function EvazEnergyAppPage() {

  useEffect(() => {
    navigate('/');
  }, []);
  return null;


  const { appState } = useContext(AppContext);
  const t = appState?.i18n?.app?.page || {};

  const testimonials = t?.testimonials?.items || [];
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const nextTestimonial = () => setCurrentTestimonial((i) => testimonials.length ? (i + 1) % testimonials.length : 0);
  const prevTestimonial = () => setCurrentTestimonial((i) => testimonials.length ? (i - 1 + testimonials.length) % testimonials.length : 0);

    useEffect(() => {
        const id = setInterval(nextTestimonial, 6000);
        return () => clearInterval(id);
    }, []);

    return (
        <Layout>
      <SEO
        title={t.seoTitle || "eVaz Energy APP"}
        description={t.description}
                meta={[
                    { property: 'og:image', content: appPromoImg },
                    { name: 'twitter:image', content: appPromoImg },
                ]}
            />

            {/* JSON-LD para melhor rich result */}
            <Helmet>
                <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "eVaz Energy APP",
            "applicationCategory": "MobileApplication",
            "operatingSystem": "iOS, Android",
            "offers": { "@type": "Offer", "price": "0", "priceCurrency": "EUR" }
          }
        `}</script>
            </Helmet>

            <PageWrap>
                {/* NOVO HERO: centralizado, com orbs suaves e headline em gradiente */}
                <Hero>
                    <span className="orb o1" />
                    <span className="orb o2" />
                    <span className="orb o3" />
                    <HeroInner>
            <HeroHeading>{t?.hero?.heading}</HeroHeading>
            <HeroLead>
              {t?.hero?.lead}
            </HeroLead>
                        <CTAWrap style={{ justifyContent: 'center' }}>
              <a href="https://apps.apple.com/pt/app/evaz-energy/id6739589936" target="_blank" rel="noopener noreferrer">
                <img src={appStore} alt={t?.hero?.appleAlt || 'App Store'} />
                            </a>
              <a href="https://play.google.com/store/apps/details?id=com.evazenergy.app" target="_blank" rel="noopener noreferrer">
                <img src={googlePlay} alt={t?.hero?.googleAlt || 'Google Play'} />
                            </a>
                        </CTAWrap>
                    </HeroInner>
                </Hero>

                {/* SHOWCASE institucional com ecrãs em destaque */}
                <Showcase>
                    <ShowcaseInner>
                        <div>
              <KickerPill>{t?.showcase?.kicker}</KickerPill>
              <ShowcaseTitle>{t?.showcase?.title}</ShowcaseTitle>
                            <ShowcaseText>
                <p>{t?.showcase?.p1}</p>
                <p>{t?.showcase?.p2}</p>
                            </ShowcaseText>
                        </div>
                    </ShowcaseInner>
                    <ShowcaseSummary>
            <Chips>
              {(t?.showcase?.chips || []).map((c, i) => (<Chip key={i}>{c}</Chip>))}
            </Chips>
                    </ShowcaseSummary>
          <ScreensBar>
            <img src={appPromoImg} alt={t?.showcase?.screensAlt} loading="lazy" decoding="async" />
          </ScreensBar>
                </Showcase>

                <Section>
          <SectionTitle>{t?.features?.title}</SectionTitle>
          <Features>
            {(t?.features?.items || []).map((item, idx) => {
              const icons = [
                <FlashOnIcon key="i0" />, <MapIcon key="i1" />, <EuroSymbolIcon key="i2" />, <LockIcon key="i3" />, <NotificationsActiveIcon key="i4" />, <HeadsetMicIcon key="i5" />
              ];
              return (
                <GlassCard key={idx}>
                  <div className="icon">{icons[idx % icons.length]}</div>
                  <h3>{item.title}</h3>
                  <p>{item.desc}</p>
                </GlassCard>
              );
            })}
          </Features>
                </Section>

                {/* Callout especial para quem já tem cartão eVaz */}
                <Section>
                    <CardCallout id="callout-cartao">
                        <div className="iconCircle" aria-hidden>
                            <CreditCardIcon />
                        </div>
                        <div>
                            <div className="kicker">{t?.cardCallout?.kicker}</div>
                            <h3>{t?.cardCallout?.title}</h3>
                            <p>{t?.cardCallout?.text}</p>
                        </div>
            <CTAWrap>
              <a href="https://apps.apple.com/pt/app/evaz-energy/id6739589936" target="_blank" rel="noopener noreferrer">
                <img src={appStore} alt={t?.hero?.appleAlt || 'App Store'} loading="lazy" decoding="async" />
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.evazenergy.app" target="_blank" rel="noopener noreferrer">
                <img src={googlePlay} alt={t?.hero?.googleAlt || 'Google Play'} loading="lazy" decoding="async" />
              </a>
            </CTAWrap>
                    </CardCallout>
                </Section>



                <Section>
          <SectionTitle>{t?.steps?.title}</SectionTitle>
                    <Stepper>
            {(t?.steps?.items || []).map((s, i) => (
              <Step key={i}>
                <StepNumber>{i + 1}</StepNumber>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </Step>
            ))}
                    </Stepper>
                </Section>

                <Section>
          <SectionTitle>{t?.testimonials?.title}</SectionTitle>
                    <SliderFrame>
      {testimonials.map((it, idx) => (
              <Slide key={idx} active={idx === currentTestimonial}>
                “{it.quote}”
                <footer style={{ marginTop: 10, color: '#94a3b8' }}>— {it.author}</footer>
              </Slide>
            ))}
                        <NavBtn className="prev" aria-label="Anterior" onClick={prevTestimonial}>
                            <ChevronLeftIcon />
                        </NavBtn>
                        <NavBtn className="next" aria-label="Seguinte" onClick={nextTestimonial}>
                            <ChevronRightIcon />
                        </NavBtn>
                    </SliderFrame>
                    <Dots>
      {testimonials.map((_, i) => (
              <Dot key={i} active={i === currentTestimonial} aria-label={`Ir para testemunho ${i + 1}`} onClick={() => setCurrentTestimonial(i)} />
            ))}
                    </Dots>
                </Section>

                <Section>
          <SectionTitle>{t?.faq?.title}</SectionTitle>
                    <FaqList>
            {(t?.faq?.items || []).map((f, i) => (
              <FaqItem key={i}>
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </FaqItem>
            ))}
                    </FaqList>
                </Section>

                {/* Reforço final com botões de instalação */}
                <Section style={{ textAlign: 'center', marginBottom: 80 }}>
                    <SectionTitle style={{ marginBottom: 16 }}>{t?.finalCta?.title}</SectionTitle>
                    <p style={{ color: '#4b5563' }}>{t?.finalCta?.text}</p>
          <CTAWrap style={{ justifyContent: 'center', marginTop: 16 }}>
            <a href="https://apps.apple.com/pt/app/evaz-energy/id6739589936" target="_blank" rel="noopener noreferrer">
              <img src={appStore} alt={t?.hero?.appleAlt || 'App Store'} loading="lazy" decoding="async" />
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.evazenergy.app" target="_blank" rel="noopener noreferrer">
              <img src={googlePlay} alt={t?.hero?.googleAlt || 'Google Play'} loading="lazy" decoding="async" />
            </a>
          </CTAWrap>
                </Section>

                <footer>
                    <Footer />
                </footer>
            </PageWrap>
        </Layout>
    );
}
